import {
  DollarSign,
  FileText,
  Trash2,
  Check,
  X,
  UserPlus,
  Users,
  Zap,
  Settings,
  CalendarCheck,
  Link2,
  Link2Off,
  FileSignature,
  CheckCircle2,
  MessageSquare,
  Mail,
  MapPin,
  Home,
  Merge,
  BadgeCheck,
  type LucideIcon,
} from "lucide-react";

import { formatDollars } from "@/shared/utils/formatters";
import type { ActionType, ActivityLogEntry } from "@/shared/types/activityLog";

export interface ActivityIconStyle {
  Icon: LucideIcon;
  bg: string;
  fg: string;
}

export const ACTIVITY_ICON_MAP: Record<ActionType, ActivityIconStyle> = {
  uploaded_document: { Icon: FileText, bg: "bg-blue-50", fg: "text-blue-600" },
  updated_document: { Icon: MessageSquare, bg: "bg-stone-100", fg: "text-stone-700" },
  deleted_document: { Icon: Trash2, bg: "bg-red-50", fg: "text-red-600" },
  added_estate_item: { Icon: DollarSign, bg: "bg-stone-100", fg: "text-stone-700" },
  updated_estate_item: { Icon: DollarSign, bg: "bg-stone-100", fg: "text-stone-700" },
  approved_estate_item: { Icon: Check, bg: "bg-emerald-50", fg: "text-emerald-600" },
  declined_estate_item: { Icon: X, bg: "bg-red-100", fg: "text-red-600" },
  deleted_estate_item: { Icon: Trash2, bg: "bg-red-50", fg: "text-red-600" },
  added_contact: { Icon: UserPlus, bg: "bg-violet-50", fg: "text-violet-600" },
  updated_contact: { Icon: Users, bg: "bg-violet-50", fg: "text-violet-600" },
  deleted_contact: { Icon: Trash2, bg: "bg-red-50", fg: "text-red-600" },
  invited_contact: { Icon: UserPlus, bg: "bg-violet-50", fg: "text-violet-600" },
  accepted_invitation: { Icon: Check, bg: "bg-emerald-50", fg: "text-emerald-600" },
  connected_plaid_account: { Icon: Link2, bg: "bg-emerald-50", fg: "text-emerald-600" },
  disconnected_plaid_account: { Icon: Link2Off, bg: "bg-stone-100", fg: "text-stone-700" },
  signed_tpa: { Icon: FileSignature, bg: "bg-blue-50", fg: "text-blue-600" },
  revoked_tpa: { Icon: FileSignature, bg: "bg-red-50", fg: "text-red-600" },
  reactivated_tpa: { Icon: FileSignature, bg: "bg-emerald-50", fg: "text-emerald-600" },
  booked_meeting: { Icon: CalendarCheck, bg: "bg-blue-50", fg: "text-blue-600" },
  submitted_task: { Icon: Check, bg: "bg-stone-100", fg: "text-stone-700" },
  submitted_elayne_task: { Icon: Zap, bg: "bg-violet-50", fg: "text-violet-600" },
  completed_task: { Icon: CheckCircle2, bg: "bg-emerald-50", fg: "text-emerald-600" },
  completed_module: { Icon: CheckCircle2, bg: "bg-emerald-50", fg: "text-emerald-600" },
  updated_user_profile: { Icon: Settings, bg: "bg-stone-100", fg: "text-stone-700" },
  updated_deceased_profile: { Icon: Settings, bg: "bg-stone-100", fg: "text-stone-700" },
  updated_relationship: { Icon: Users, bg: "bg-violet-50", fg: "text-violet-600" },
  requested_account_deletion: { Icon: Trash2, bg: "bg-red-50", fg: "text-red-600" },
  tier_purchased: { Icon: Zap, bg: "bg-emerald-50", fg: "text-emerald-600" },
  tier_upgraded: { Icon: Zap, bg: "bg-emerald-50", fg: "text-emerald-600" },
  address_link_repointed: { Icon: MapPin, bg: "bg-blue-50", fg: "text-blue-600" },
  address_corrected: { Icon: MapPin, bg: "bg-stone-100", fg: "text-stone-700" },
  address_merged: { Icon: Merge, bg: "bg-stone-100", fg: "text-stone-700" },
  address_validated: { Icon: BadgeCheck, bg: "bg-emerald-50", fg: "text-emerald-600" },
  property_updated: { Icon: Home, bg: "bg-stone-100", fg: "text-stone-700" },
  sent_estate_account_reminder: { Icon: Mail, bg: "bg-blue-50", fg: "text-blue-600" },
};

export const ACTIVITY_ICON_FALLBACK: ActivityIconStyle = {
  Icon: Zap,
  bg: "bg-stone-100",
  fg: "text-stone-700",
};

export const ACTIVITY_ICON_ADMIN: ActivityIconStyle = {
  Icon: Users,
  bg: "bg-violet-50",
  fg: "text-violet-600",
};

const ACTIVITY_ICON_NOTES: ActivityIconStyle = {
  Icon: MessageSquare,
  bg: "bg-violet-50",
  fg: "text-violet-600",
};

export const getActivityIcon = (entry: ActivityLogEntry): ActivityIconStyle => {
  if (entry.actor_type === "admin") return ACTIVITY_ICON_ADMIN;
  if (entry.action_type === "updated_estate_item") {
    const after = entry.metadata.after as Record<string, unknown> | undefined;
    const before = entry.metadata.before as Record<string, unknown> | undefined;
    if (after?.notes && after.notes !== before?.notes) return ACTIVITY_ICON_NOTES;
  }
  return ACTIVITY_ICON_MAP[entry.action_type] ?? ACTIVITY_ICON_FALLBACK;
};

export const getActorLabel = (entry: ActivityLogEntry): string => {
  if (entry.actor_type === "admin") return "Elayne";
  if (entry.actor_type === "system") return "System";
  return entry.actor_name || entry.actor_email || "Someone";
};

const stringField = (m: Record<string, unknown>, key: string): string | null => {
  const v = m[key];
  return typeof v === "string" && v.length > 0 ? v : null;
};

const truncate = (s: string, max = 80): string => (s.length > max ? `${s.slice(0, max)}…` : s);

// Address audit rows nest the snapshot under before/after alongside the id.
const renderedAddress = (m: Record<string, unknown>, key: string): string | null => {
  const side = m[key] as { rendered?: unknown } | null | undefined;
  const rendered = side?.rendered;
  return typeof rendered === "string" && rendered.length > 0 ? rendered : null;
};

export interface RenderedActivityRow {
  title: React.ReactNode;
  subtitle?: string | null;
}

export interface RenderActivityRowOptions {
  onDocumentClick?: (entry: ActivityLogEntry) => void;
  onEstateItemClick?: (entry: ActivityLogEntry) => void;
}

export const renderActivityRow = (
  entry: ActivityLogEntry,
  options: RenderActivityRowOptions = {},
): RenderedActivityRow => {
  const m = entry.metadata;
  const actor = getActorLabel(entry);
  const Actor = <span className="font-medium text-primary">{actor}</span>;

  const renderEntityLink = (
    name: string,
    entityType: string,
    onClick?: (entry: ActivityLogEntry) => void,
  ) =>
    onClick && entry.entity_type === entityType && entry.entity_id ? (
      <button
        type="button"
        onClick={() => onClick(entry)}
        className="text-primary hover:underline focus-visible:underline focus-visible:decoration-2 focus-visible:outline-none"
      >
        {name}
      </button>
    ) : (
      <span className="text-primary">{name}</span>
    );

  const renderDocumentName = (name: string) =>
    renderEntityLink(name, "document", options.onDocumentClick);

  const renderItemName = (name: string) =>
    renderEntityLink(name, "estate_item", options.onEstateItemClick);

  const itemName = stringField(m, "item_name") || stringField(m, "category") || "an estate item";

  switch (entry.action_type) {
    case "uploaded_document": {
      const name = stringField(m, "display_name") || stringField(m, "file_name") || "a document";
      return {
        title: (
          <span>
            {Actor} uploaded {renderDocumentName(name)}
          </span>
        ),
      };
    }
    case "updated_document": {
      const name = stringField(m, "display_name") || stringField(m, "file_name") || "a document";
      return {
        title: (
          <span>
            {Actor} updated {renderDocumentName(name)}
          </span>
        ),
      };
    }
    case "deleted_document": {
      const name = stringField(m, "display_name") || stringField(m, "file_name") || "a document";
      return {
        title: (
          <span>
            {Actor} deleted <span className="text-primary">{name}</span>
          </span>
        ),
      };
    }
    case "added_estate_item": {
      const balance =
        typeof m.balance === "number"
          ? formatDollars({ amount: m.balance, rounding: "whole" })
          : null;
      return {
        title: (
          <span>
            {Actor} added {renderItemName(itemName)}
          </span>
        ),
        subtitle: balance ? `Value ${balance}` : null,
      };
    }
    case "updated_estate_item": {
      const before = m.before as Record<string, unknown> | undefined;
      const after = m.after as Record<string, unknown> | undefined;
      const beforeBal =
        before && typeof before.balance === "number"
          ? formatDollars({ amount: before.balance, rounding: "whole" })
          : null;
      const afterBal =
        after && typeof after.balance === "number"
          ? formatDollars({ amount: after.balance, rounding: "whole" })
          : null;
      const subtitle =
        beforeBal && afterBal && beforeBal !== afterBal ? `Value ${beforeBal} → ${afterBal}` : null;
      return {
        title: (
          <span>
            {Actor} updated {renderItemName(itemName)}
          </span>
        ),
        subtitle,
      };
    }
    case "approved_estate_item": {
      return {
        title: (
          <span>
            {Actor} approved {renderItemName(itemName)} for inclusion in the inventory
          </span>
        ),
      };
    }
    case "declined_estate_item": {
      return {
        title: (
          <span>
            {Actor} declined {renderItemName(itemName)}
          </span>
        ),
      };
    }
    case "deleted_estate_item": {
      return {
        title: (
          <span>
            {Actor} removed <span className="text-primary">{itemName}</span>
          </span>
        ),
      };
    }
    case "invited_contact": {
      const email = stringField(m, "email");
      const role = stringField(m, "role");
      const name = stringField(m, "name") || email || "a contact";
      return {
        title: (
          <span>
            {Actor} invited <span className="text-primary">{name}</span>
            {role && <> as {role}</>}
          </span>
        ),
        subtitle: email ? `${email} · invitation pending` : null,
      };
    }
    case "added_contact": {
      const name = stringField(m, "name") || "a contact";
      const relationship = stringField(m, "relationship");
      return {
        title: (
          <span>
            {Actor} added <span className="text-primary">{name}</span>
          </span>
        ),
        subtitle: relationship,
      };
    }
    case "updated_contact":
      return { title: <span>{Actor} updated a contact</span> };
    case "deleted_contact":
      return { title: <span>{Actor} removed a contact</span> };
    case "accepted_invitation":
      return { title: <span>{Actor} accepted an invitation</span> };
    case "connected_plaid_account": {
      const inst = stringField(m, "institution_name") || stringField(m, "institution");
      return {
        title: (
          <span>
            {Actor} connected{" "}
            {inst ? <span className="text-primary">{inst}</span> : "a Plaid account"}
          </span>
        ),
      };
    }
    case "disconnected_plaid_account": {
      const inst = stringField(m, "institution_name") || stringField(m, "institution");
      return {
        title: (
          <span>
            {Actor} disconnected{" "}
            {inst ? <span className="text-primary">{inst}</span> : "a Plaid account"}
          </span>
        ),
      };
    }
    case "signed_tpa":
      return { title: <span>{Actor} signed the TPA</span> };
    case "revoked_tpa": {
      const sigType = stringField(m, "signature_type");
      const label = sigType === "irs" ? "IRS authorization" : "TPA";
      return {
        title: (
          <span>
            {Actor} revoked the {label}
          </span>
        ),
      };
    }
    case "reactivated_tpa": {
      const sigType = stringField(m, "signature_type");
      const label = sigType === "irs" ? "IRS authorization" : "TPA";
      return {
        title: (
          <span>
            {Actor} reactivated the {label}
          </span>
        ),
      };
    }
    case "booked_meeting":
      return { title: <span>{Actor} booked a meeting</span> };
    case "completed_task":
    case "submitted_task":
    case "completed_module": {
      const taskName = stringField(m, "task_name") || stringField(m, "module_name");
      const verb = entry.action_type === "submitted_task" ? "submitted" : "completed";
      return {
        title: (
          <span>
            {Actor} {verb} {taskName ? <span className="text-primary">{taskName}</span> : "a task"}
          </span>
        ),
      };
    }
    case "address_link_repointed": {
      const purpose = (stringField(m, "purpose") ?? "").replace(/_/g, " ") || "address";
      const before = renderedAddress(m, "before");
      const after = renderedAddress(m, "after");
      const subtitle = after
        ? before
          ? `${before} → ${after}`
          : after
        : before
          ? `Removed ${before}`
          : null;
      return {
        title: (
          <span>
            {Actor} changed the <span className="text-primary">{purpose}</span> address
          </span>
        ),
        subtitle: subtitle ? truncate(subtitle) : null,
      };
    }
    case "address_corrected": {
      const rendered = renderedAddress(m, "after") || stringField(m, "rendered");
      const fields = Object.keys((m.changed as Record<string, unknown>) ?? {});
      const blastRadius =
        typeof m.affected_link_count === "number" && m.affected_link_count > 1
          ? ` (${m.affected_link_count} links affected)`
          : "";
      return {
        title: (
          <span>
            {Actor} corrected an address{" "}
            {rendered ? <span className="text-primary">{truncate(rendered)}</span> : null}
          </span>
        ),
        subtitle: fields.length ? `${fields.join(", ")}${blastRadius}` : blastRadius || null,
      };
    }
    case "address_merged": {
      const count = typeof m.links_repointed === "number" ? m.links_repointed : null;
      return {
        title: <span>{Actor} merged two addresses</span>,
        subtitle: count === null ? null : `${count} ${count === 1 ? "link" : "links"} repointed`,
      };
    }
    case "address_validated": {
      const from = stringField(m, "status_before");
      const to = stringField(m, "status_after");
      const rendered = stringField(m, "rendered");
      return {
        title: (
          <span>
            {Actor} validated{" "}
            {rendered ? <span className="text-primary">{truncate(rendered)}</span> : "an address"}
          </span>
        ),
        subtitle: from && to ? `${from} → ${to}` : null,
      };
    }
    case "sent_estate_account_reminder":
      return { title: <span>{Actor} sent an estate account reminder</span> };
    case "property_updated": {
      const name = stringField(m, "item_name") || "a property";
      const changed = (m.changed as Record<string, unknown> | undefined) ?? {};
      const address = changed.address as { before?: unknown; after?: unknown } | undefined;
      const from = typeof address?.before === "string" ? address.before : null;
      const to = typeof address?.after === "string" ? address.after : null;
      const subtitle = to ? (from ? `${from} → ${to}` : to) : from ? `Removed ${from}` : null;
      return {
        title: (
          <span>
            {Actor} changed the address of {renderItemName(name)}
          </span>
        ),
        subtitle: subtitle ? truncate(subtitle) : null,
      };
    }
    default: {
      const note = stringField(m, "note");
      const fallback = entry.action_type.replace(/^(admin_|user_|system_)/, "").replace(/_/g, " ");
      return {
        title: (
          <span>
            {Actor} <span className="text-secondary">{fallback}</span>
          </span>
        ),
        subtitle: note ? truncate(note) : null,
      };
    }
  }
};
